import { Trans } from '@lingui/macro'

type Operator = typeof operators[number]
type AdditionalOperator = typeof additionalOperators[number]

export type AnyOperator = Operator | AdditionalOperator

export const scoringCategoryNameToKey = {
  'Property Management': 'property',
  'Financial Managment': 'financial',
  Governance: 'governance',
  'Social and community relations': 'social'
}

export const types = [
  { trans: <Trans>Category</Trans>, value: 'category' },
  { trans: <Trans>Question</Trans>, value: 'question' },
  { trans: <Trans>Logical</Trans>, value: 'logical' },
  { trans: <Trans>Template</Trans>, value: 'template' }
]

export const templateCategories = [
  { trans: <Trans>None</Trans>, value: 'None' },
  { trans: <Trans>Property Management</Trans>, value: 'Property Management' },
  { trans: <Trans>Financial Managment</Trans>, value: 'Financial Managment' },
  { trans: <Trans>Governance</Trans>, value: 'Governance' },
  {
    trans: <Trans>Social and community relations</Trans>,
    value: 'Social and community relations'
  }
]

export const categories = [
  { trans: <Trans>Property Management</Trans>, value: 'property' },
  { trans: <Trans>Financial Managment</Trans>, value: 'financial' },
  { trans: <Trans>Governance</Trans>, value: 'governance' },
  { trans: <Trans>Social and community relations</Trans>, value: 'social' }
]

export const operators = [
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'eq', label: '==' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' }
]

export const additionalOperators = [
  { value: 'all', label: <Trans>all</Trans> },
  { value: 'any', label: <Trans>any</Trans> },
  { value: 'empty', label: <Trans>ACTION_TEMPLATE_CONDITION_EMPTY_LABEL</Trans> },
  { value: 'notEmpty', label: <Trans>ACTION_TEMPLATE_CONDITION_NOT_EMPTY_LABEL</Trans> }
]

interface QuestionOption {
  id?: string
  value?: string
  label?: string
}

interface TitleValue {
  [language: string]: string
}

interface ProcessedQuestion {
  id: string
  order: number
  options: QuestionOption[]
  parentQuestion?: string
  childIndex?: number
  children: string[]
  isGroup?: boolean
  type: string
  title: JSX.Element
  titleValue: TitleValue
  templateId: string
}

interface ProcessTemplateQuestionsParams {
  questionsDetailsMap: {
    [key: string]: any
  }
  userLanguage: string
  validQuestionTypes: string[]
  selectedQuestionId?: string | null
  selectQuestion?: (question: ProcessedQuestion) => void
}

interface ProcessTemplateQuestionsResult {
  processedQuestions: ProcessedQuestion[]
  processedQuestionsMap: Record<string, ProcessedQuestion[]>
}

export function processTemplateQuestions ({
  questionsDetailsMap,
  userLanguage,
  validQuestionTypes,
  selectedQuestionId = null,
  selectQuestion = undefined
}: ProcessTemplateQuestionsParams): ProcessTemplateQuestionsResult {
  const allQuestionsDetails = Object.entries(questionsDetailsMap).flatMap(
    ([templateId, questions]) =>
      questions.map(q => ({
        ...q,
        templateId
      }))
  )

  const matchObj = {}
  const processedQuestionsMap = {}
  allQuestionsDetails.forEach(q => {
    const options = JSON.parse(
      q.item.Survey_Joins_Template_and_Question__r.records[0]
        .Item_Options__c
    )
    if (options != null) {
      const sfId = options.createdId
      matchObj[sfId] = {
        title: q.itemTranslation.titleValue[userLanguage] ?? '',
        ...matchObj[sfId]
      }
      if (options.parentQuestionId != null) {
        if (matchObj[options.parentQuestionId] != null) {
          const toCopy = matchObj[options.parentQuestionId].children ?? []
          matchObj[options.parentQuestionId].children = [sfId, ...toCopy]
        } else {
          matchObj[options.parentQuestionId] = {
            children: [sfId],
            ...matchObj[options.parentQuestionId]
          }
        }
      }
    }
  })

  const processedQuestions = allQuestionsDetails
    .map(question => {
      if (!validQuestionTypes.includes(question.item.Type__c)) {
        return null
      }

      const options = JSON.parse(
        question.item.Survey_Joins_Template_and_Question__r.records[0]
          .Item_Options__c
      )
      if (options == null) return null
      const sfId = options.createdId
      const rawOrder = question
        .item
        .Survey_Joins_Template_and_Question__r
        .records[0]
        .Order__c

      const orderBase = Number(rawOrder)
      let order = (isNaN(orderBase) ? 0 : orderBase) + 1

      let qTitle = question.itemTranslation.titleValue[userLanguage]
      const titleValue = question.itemTranslation.titleValue
      if (
        question.itemOptions.parentQuestionId != null &&
        matchObj[question.itemOptions.parentQuestionId] != null
      ) {
        order = Number(order + 0.1)
        qTitle = (
          <span>
            <b>
              [{matchObj[question.itemOptions.parentQuestionId].title}]
            </b>{' '}
            {qTitle}
          </span>
        )
      } else {
        qTitle = (
          <>
            {order}. {qTitle}
          </>
        )
      }
      if (
        question.itemOptions.parentQuestionId != null &&
        matchObj[question.itemOptions.parentQuestionId] == null
      ) {
        return null
      }
      if (
        question.itemOptions.parentQuestionId == null &&
        question.itemOptions.parentQuestionIndex != null
      ) {
        return null
      }
      const processedQuestion: ProcessedQuestion = {
        id: sfId,
        order,
        options: question.itemDetails ?? [],
        parentQuestion: question.itemOptions.parentQuestionId,
        childIndex: question.itemOptions.index,
        children: matchObj[sfId].children,
        isGroup: question.itemOptions.isGroup,
        type: question.item.Type__c,
        title: qTitle,
        titleValue,
        templateId: question.templateId
      }
      if (processedQuestion.id === selectedQuestionId) {
        if (selectQuestion != null) {
          selectQuestion(processedQuestion)
        }
      }

      if (processedQuestion.id != null) {
        if (processedQuestionsMap[question.templateId] == null) {
          processedQuestionsMap[question.templateId] = [processedQuestion]
        } else {
          processedQuestionsMap[question.templateId].push(processedQuestion)
        }
      }

      return processedQuestion
    })
    .filter((q): q is ProcessedQuestion => q !== null)
    .sort((a, b) => a.order - b.order)
  return { processedQuestions, processedQuestionsMap }
}
